import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { productApi } from '@/api/commercial/products';
import { deviceApi } from '@/api/production/devices';
import { useFormError } from '@/hooks/use-form-error';
import { toast } from '@/hooks/use-toast';
import { type CreateDeviceFormData, createDeviceSchema } from '@/schemas/production/device.schema';
import type { CreateDeviceDto } from '@/types/production/device';
import { mapProductNameToDeviceModel } from '@/utils/device-model-mapper';

export function useDeviceCreateForm() {
  const { formError, showFormError, clearFormError } = useFormError();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: () => productApi.findMany({ filter: {}, limit: 100 }),
  });

  const form = useForm<CreateDeviceFormData>({
    resolver: zodResolver(createDeviceSchema),
  });

  const mutation = useMutation({
    mutationFn: deviceApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Equipamento registrado com sucesso!',
      });
      navigate('/production/devices');
    },
    onError: () => {
      showFormError('Falha ao registrar o equipamento!');
    },
  });

  const handleSubmit = form.handleSubmit((data) => {
    const products = productsData?.docs || [];
    const product = products.find((p) => p.id === data.product_id);

    if (!product) {
      form.setError('product_id', { message: 'Produto não encontrado!' });
      return;
    }

    const deviceModel = mapProductNameToDeviceModel(product.name);

    if (!deviceModel) {
      form.setError('product_id', {
        message: `Produto "${product.name}" não possui mapeamento para modelo de dispositivo!`,
      });
      return;
    }

    const deviceData: CreateDeviceDto = {
      equipment: {
        serial: data.equipment.serial,
        firmware: 'N/A', // Default firmware value
      },
      model: deviceModel as any,
    };

    mutation.mutate(deviceData);
  });

  return {
    formError,
    clearFormError,
    form,
    handleSubmit,
    isSubmitting: mutation.isPending,
    products: productsData?.docs || [],
  };
}
